import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import api from '../../../../../api/client';

import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const CurrentBalanceInfoModal = ({ isOpen, onClose, numericData }) => {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!isOpen) return;

        const fetchCurrentBalance = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.get('credits/balance/');
                setData(response.data);
            } catch (err) {
                console.error('Error fetching current balance:', err);
                setError(t('dashboard.failedToLoadCredits'));
            } finally {
                setLoading(false);
            }
        };

        fetchCurrentBalance();
    }, [isOpen, t]);

    const formatDate = (dateString) => {
        if (!dateString) return t('dashboard.noExpiry');
        const locale = i18n.language === 'hi' ? 'hi-IN' : i18n.language || 'en-US';
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!isOpen) return null;

    // Calculations from V2 bucket data
    const totalActiveOriginal = data?.contributing_buckets?.reduce((sum, b) => sum + (b.original_amount || 0), 0) || 0;
    const totalActiveUsed = data?.contributing_buckets?.reduce((sum, b) => sum + ((b.original_amount || 0) - (b.remaining_balance || 0)), 0) || 0;
    const currentBalanceValue = data?.current_balance || 0;

    // Show red expiry alert only within 5 days
    const daysUntilExpiry = data?.next_expiry
        ? Math.ceil((new Date(data.next_expiry) - new Date()) / (1000 * 60 * 60 * 24))
        : null;
    // Alert color: red only within 5 days, violet otherwise
    const showExpiryAlert = !!data?.next_expiry;
    const isExpiryUrgent = daysUntilExpiry !== null && daysUntilExpiry <= 5;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div
                className="bg-white rounded-3xl md:w-[70%] lg:w-[55%] w-[95%] max-h-[92vh] flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white rounded-t-3xl">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-violet-50 text-violet-600">
                            <AccountBalanceWalletIcon fontSize="medium" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                                {t('dashboard.currentBalanceBreakdown')}
                            </h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {t('dashboard.currentBalanceBreakdownDesc')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-900 transition-all duration-200"
                    >
                        <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto bg-gray-50/50 p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mb-4"></div>
                            <p className="text-gray-500 font-medium">{t('dashboard.loadingCreditsHistory')}</p>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center my-10 max-w-md mx-auto">
                            <span className="text-3xl">⚠️</span>
                            <h3 className="text-red-800 font-semibold text-lg mt-2">{t('common.error')}</h3>
                            <p className="text-red-600 text-sm mt-1">{error}</p>
                            <button
                                onClick={onClose}
                                className="mt-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm font-medium"
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    ) : data ? (
                        <>
                            {/* Next Expiry Warn Callout */}
                            {showExpiryAlert && (
                                <div className={`relative overflow-hidden rounded-2xl p-4 mb-6 flex items-center gap-4 shadow-lg ${
                                    isExpiryUrgent
                                        ? 'bg-gradient-to-r from-red-500 to-rose-600 shadow-red-100'
                                        : 'bg-gradient-to-r from-violet-600 to-indigo-600 shadow-violet-100'
                                }`}>
                                    <div className="absolute inset-0 opacity-10 pointer-events-none" style={{backgroundImage: 'radial-gradient(circle at 80% 50%, white 0%, transparent 60%)'}} />
                                    <div className="p-2.5 rounded-xl bg-white/20 text-white shrink-0 animate-pulse">
                                        <WarningAmberIcon fontSize="small" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white uppercase tracking-wider">
                                                Expiry Alert
                                            </span>
                                        </div>
                                        <p className="text-white/90 text-xs leading-relaxed">
                                            {t('dashboard.nextExpiryWarn', {
                                                amount: data.next_expiry_amount,
                                                date: formatDate(data.next_expiry)
                                            })}
                                        </p>
                                    </div>
                                </div>
                            )}


                            {/* Math Equation Visual Section */}
                            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm mb-6">
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                                    {t('dashboard.calculationFormula')}
                                </h3>

                                <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-2">
                                    {/* Active Buckets original total */}
                                    <div className="bg-gradient-to-br from-green-50/50 to-green-100/10 border border-green-200/60 rounded-2xl p-5 flex-1 w-full text-center">
                                        <div className="p-1.5 rounded-full bg-green-100 text-green-700 w-fit mx-auto mb-2">
                                            <AddCircleOutlineIcon fontSize="small" />
                                        </div>
                                        <div className="text-2xl font-bold text-green-700">{totalActiveOriginal}</div>
                                        <div className="text-xs font-semibold text-green-800 mt-1">{t('dashboard.activeBucketsCredits')}</div>
                                        <div className="text-[10px] text-green-600 mt-0.5">{t('dashboard.activeBucketsCreditsDesc')}</div>
                                    </div>

                                    {/* Minus Indicator */}
                                    <div className="p-2 rounded-full bg-gray-100 text-gray-500 shadow-sm">
                                        <RemoveCircleOutlineIcon fontSize="medium" />
                                    </div>

                                    {/* Active Buckets used total */}
                                    <div className="bg-gradient-to-br from-orange-50/50 to-orange-100/10 border border-orange-200/60 rounded-2xl p-5 flex-1 w-full text-center">
                                        <div className="p-1.5 rounded-full bg-orange-100 text-orange-700 w-fit mx-auto mb-2">
                                            <RemoveCircleOutlineIcon fontSize="small" />
                                        </div>
                                        <div className="text-2xl font-bold text-orange-700">{totalActiveUsed}</div>
                                        <div className="text-xs font-semibold text-orange-800 mt-1">{t('dashboard.usedActiveCredits')}</div>
                                        <div className="text-[10px] text-orange-600 mt-0.5">{t('dashboard.usedActiveCreditsDesc')}</div>
                                    </div>

                                    {/* Equals Indicator */}
                                    <div className="p-2 rounded-full bg-gray-100 text-gray-500 shadow-sm">
                                        <DragHandleIcon fontSize="medium" />
                                    </div>

                                    {/* Current available balance */}
                                    <div className="bg-gradient-to-br from-violet-50/50 to-violet-100/10 border border-violet-200 rounded-2xl p-5 flex-1 w-full text-center">
                                        <div className="p-1.5 rounded-full bg-violet-100 text-violet-700 w-fit mx-auto mb-2">
                                            <AccountBalanceWalletIcon fontSize="small" />
                                        </div>
                                        <div className="text-2xl font-bold text-violet-700">{currentBalanceValue}</div>
                                        <div className="text-xs font-semibold text-violet-800 mt-1">{t('dashboard.availableBalance')}</div>
                                        <div className="text-[10px] text-violet-600 mt-0.5">{t('dashboard.availableBalanceDesc')}</div>
                                    </div>
                                </div>

                                {/* Raw formula representation */}
                                {data?.formula && (
                                    <div className="mt-5 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                                            Formula String:
                                        </span>
                                        <code className="px-3 py-1.5 bg-gray-50 border border-gray-200/60 rounded-lg text-sm text-gray-700 font-semibold font-mono break-all sm:text-right">
                                            {data.formula}
                                        </code>
                                    </div>
                                )}
                            </div>
                            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm mt-6">
                                {/* Active contributing buckets section */}
                                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <LocalActivityIcon className="text-violet-600" fontSize="small" />
                                    {t('dashboard.contributingPacks')}
                                </h3>

                                {data?.contributing_buckets && data.contributing_buckets.length > 0 ? (
                                    <div className="space-y-4">
                                        {data.contributing_buckets.map((bucket) => {
                                            const percentRemaining = (bucket.remaining_balance / bucket.original_amount) * 100;
                                            return (
                                                <div key={bucket.id} className="p-4 rounded-xl border border-gray-100 hover:bg-blue-50/10 transition-all duration-200">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-bold text-gray-800 text-sm">#{bucket.id}</span>
                                                                {bucket.is_daily && (
                                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800">
                                                                        Daily
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-gray-500 mt-1 font-medium">{bucket.notes || '-'}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className="text-sm font-bold text-gray-900">
                                                                {bucket.remaining_balance} / {bucket.original_amount}
                                                            </span>
                                                            <span className="text-xs text-gray-400 block font-semibold uppercase">
                                                                {t('dashboard.credits')}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Progress bar */}
                                                    <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                                                        <div
                                                            className="bg-violet-500 h-full rounded-full transition-all duration-300"
                                                            style={{ width: `${percentRemaining}%` }}
                                                        />
                                                    </div>

                                                    <div className="flex justify-between items-center mt-2.5 text-[11px] text-gray-400 font-semibold">
                                                        <span>
                                                            Created: {formatDate(bucket.created_at)}
                                                        </span>
                                                        <span className="text-orange-500">
                                                            Expires: {formatDate(bucket.expiry_date)}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="p-8 text-center text-gray-400 font-medium">
                                        No active buckets contributing to the balance.
                                    </div>
                                )}
                            </div>
                        </>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 flex justify-end bg-white rounded-b-3xl">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200 shadow-sm"
                    >
                        {t('common.cancel')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CurrentBalanceInfoModal;
